/**
 * InviteFiltersDrawer
 *
 * Drawer with filter fields for the invitations table:
 * email, status (multi-select), and sent-date range.
 *
 * ## Component Hierarchy
 * - **Level**: Molecule
 * - **Feature**: Users
 *
 * @module features/users/components/ui/molecules/InviteFiltersDrawer
 */
import React from 'react';
import { Drawer, Input, Select, Space, Typography } from 'antd';

import { useLanguage } from '@/shared/hooks/useLanguage';
import type {
  InviteFilters,
  InviteStatus,
} from '@/features/users/types/users.types';

import './InviteFiltersDrawer.css';
import { Button } from '@/shared/components/ui/atoms';

export interface InviteFiltersDrawerProps {
  /** Whether the drawer is visible */
  open: boolean;
  /** Draft filter values (controlled) */
  draftFilters: InviteFilters;
  /** Setter for draft filter values */
  onDraftChange: (filters: InviteFilters) => void;
  /** Called to close the drawer without applying */
  onClose: () => void;
  /** Called to clear all filters */
  onClear: () => void;
  /** Called to apply the current draft filters */
  onApply: () => void;
}

/**
 * InviteFiltersDrawer Component
 *
 * @param props - Component properties
 * @returns A Drawer with invite-specific filter controls
 *
 * @example
 * ```tsx
 * <InviteFiltersDrawer
 *   open={isOpen}
 *   draftFilters={draftFilters}
 *   onDraftChange={setDraftFilters}
 *   onClose={handleClose}
 *   onClear={handleClear}
 *   onApply={handleApply}
 * />
 * ```
 */
export const InviteFiltersDrawer: React.FC<InviteFiltersDrawerProps> = ({
  open,
  draftFilters,
  onDraftChange,
  onClose,
  onClear,
  onApply,
}) => {
  const { t } = useLanguage();

  return (
    <Drawer
      title={t('Filter')}
      width={630}
      open={open}
      onClose={onClose}
      placement="right"
      extra={
        <Button variant="link" onClick={onClear}>
          {t('Clear')}
        </Button>
      }
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button variant="ghost" onClick={onClear}>
            {t('Clear')}
          </Button>
          <Space>
            <Button variant="ghost" onClick={onClose}>
              {t('Close')}
            </Button>
            <Button variant="primary" onClick={onApply}>
              {t('Apply filters')}
            </Button>
          </Space>
        </Space>
      }
    >
      <div className="invite-filters-drawer">
        <div>
          <Typography.Text>{t('Email')}</Typography.Text>
          <Input
            value={draftFilters.email}
            onChange={(e) =>
              onDraftChange({ ...draftFilters, email: e.target.value })
            }
            placeholder={t('Enter email')}
            aria-label={t('Filter by email')}
          />
        </div>

        <div>
          <Typography.Text>{t('Status')}</Typography.Text>
          <Select
            mode="multiple"
            allowClear
            value={draftFilters.statuses}
            onChange={(value) =>
              onDraftChange({
                ...draftFilters,
                statuses: value as InviteStatus[],
              })
            }
            options={[
              { label: t('Pending'), value: 'PENDING' },
              { label: t('Expired'), value: 'EXPIRED' },
              { label: t('Registered'), value: 'REGISTERED' },
            ]}
            placeholder={t('Select status')}
            aria-label={t('Filter by status')}
          />
        </div>
      </div>
    </Drawer>
  );
};

export default InviteFiltersDrawer;
